import React from 'react';
import { Alert } from 'react-bootstrap';

/**
 * EVERY FIELD-LEVEL MESSAGE THE SERVER SENT, WHERE THE OFFICE CAN SEE IT.
 *
 * The API answers a refused save with `{ message, errors: [{ field, message }] }`
 * and the forms render each one through `FormControlFeedback`, against the
 * field it names. That works only for a path some input on screen happens to
 * ask for. Anything else is DROPPED SILENTLY:
 *
 *   - a path no field claims — `operators` on the booking form, which is the
 *     one message a new expense is refused for;
 *   - a root-level issue, whose path formats to the empty string (the API's
 *     `formatIssuePath([])`), so no field can ever claim it;
 *   - a field this booking type hides, which is most of the form on an
 *     expense.
 *
 * In every one of those cases all the office got was the generic banner —
 * "An error occured / The validation has failed" — while the server had
 * already said exactly what was wrong and where. Listing them is the
 * backstop: a message cannot go missing because nobody thought to render a
 * feedback for its path.
 *
 * Deliberately shown ALONGSIDE the inline feedback rather than instead of
 * it, and without trying to work out which ones were claimed. Knowing that
 * would mean every `FormControlFeedback` registering itself somewhere, which
 * is a second mechanism to keep in step with the first; a message appearing
 * twice costs the reader nothing, and a message appearing nowhere is the bug
 * this exists for. The list is the checklist, the inline copy is where to
 * fix it.
 */
export default function ValidationErrorList({ errors }) {
  const listed = (Array.isArray(errors) ? errors : []).filter((e) => e?.message);

  if (!listed.length) return null;

  return (
    <Alert variant="danger" data-testid="validation-error-list">
      <Alert.Heading className="h6">Please correct the following</Alert.Heading>
      <ul className="mb-0 ps-3">
        {listed.map((e) => (
          <li key={`${e.field || ''}:${e.message}`}>
            {e.field && (
              <>
                <strong>{e.field}</strong>
                {' — '}
              </>
            )}
            {e.message}
          </li>
        ))}
      </ul>
    </Alert>
  );
}
